import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Button, Flex, Text, message } from '@nula/core-ui';
import { isDesktop } from '@/lib/platform';
import { shareBaseUrl } from '@/lib/sharing/utils/share-url';
import { useReferral } from './use-subscription';
import styles from './referral-section.module.css';

interface ReferralSectionProps {
  /** `window.location.origin` at the call site. Injected for testability, mirrors `shareBaseUrl`'s own design. */
  locationOrigin?: string;
}

function formatCredit(cents: number): string {
  return `$${Math.round(cents / 100)}`;
}

export function ReferralSection({ locationOrigin = window.location.origin }: ReferralSectionProps) {
  const { data, isLoading } = useReferral();
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  if (isLoading || !data) return null;

  // Never hardcode a domain — the server deliberately sends no URL, and a
  // desktop `tauri://localhost` origin would be unopenable by the recipient.
  const origin = shareBaseUrl({
    isDesktop,
    webOrigin: import.meta.env.VITE_DESKTOP_WEB_ORIGIN ?? '',
    locationOrigin,
  });
  const host = origin.replace(/^https?:\/\//, '');
  const shareUrl = `${origin}/r/${data.code}`;

  const handleCopyCode = () => {
    if (!navigator.clipboard) {
      Sentry.captureException(new Error('Clipboard API unavailable'));
      message.error("Couldn't copy the code. Please try again.");
      return;
    }
    navigator.clipboard.writeText(data.code).then(
      () => {
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 2000);
      },
      (err: unknown) => {
        Sentry.captureException(err);
        message.error("Couldn't copy the code. Please try again.");
      },
    );
  };

  const handleCopyLink = () => {
    if (!navigator.clipboard) {
      Sentry.captureException(new Error('Clipboard API unavailable'));
      message.error("Couldn't copy the link. Please try again.");
      return;
    }
    navigator.clipboard.writeText(shareUrl).then(
      () => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      },
      (err: unknown) => {
        Sentry.captureException(err);
        message.error("Couldn't copy the link. Please try again.");
      },
    );
  };

  return (
    <Flex vertical gap="sm" className={styles.section}>
      <Flex vertical gap="xs">
        <Text size="md" strong>
          Refer a friend
        </Text>
        <Text size="sm" type="secondary">
          They get their first month free. You get $20 credit when they subscribe.
        </Text>
      </Flex>

      <Flex gap="sm" className={styles.codeRow}>
        <Flex align="center" justify="center" className={styles.codeBox}>
          <Text className={styles.code}>{data.code}</Text>
        </Flex>
        <Button variant="default" onClick={handleCopyCode}>
          {codeCopied ? 'Copied' : 'Copy'}
        </Button>
      </Flex>

      <Flex align="center" gap="sm" className={styles.linkRow}>
        <Text size="sm" type="secondary" className={styles.url}>
          {host}/r/{data.code}
        </Text>
        <Button variant="link" size="sm" className={styles.copyLink} onClick={handleCopyLink}>
          {linkCopied ? 'Copied' : 'Copy link'}
        </Button>
      </Flex>

      <Flex className={styles.stats}>
        <Flex vertical gap="xs" className={styles.stat}>
          <Text className={styles.statNumber} data-zero={data.redeemed === 0 || undefined}>
            {data.redeemed}
          </Text>
          <Text size="xs" type="secondary">
            Referrals
          </Text>
        </Flex>
        <Flex vertical gap="xs" className={styles.stat}>
          <Text className={styles.statNumber} data-zero={data.converted === 0 || undefined}>
            {data.converted}
          </Text>
          <Text size="xs" type="secondary">
            Subscribed
          </Text>
        </Flex>
        <Flex vertical gap="xs" className={styles.stat}>
          <Text className={styles.statNumber} data-earn={data.creditCents > 0 || undefined}>
            {formatCredit(data.creditCents)}
          </Text>
          <Text size="xs" type="secondary">
            Credit earned
          </Text>
        </Flex>
      </Flex>

      <Text size="xs" type="secondary" className={styles.hint}>
        Credit applies automatically to your next invoice.
      </Text>
    </Flex>
  );
}
